/**
 * AiContext.js — Serializes the current canvas state for the API request.
 * Reads from window.epxFlow (set in index.js after initModeler).
 */

const MAX_XML_CHARS = 40_000;

/**
 * Builds the context object for an AssistRequest.
 * @param {boolean} includeSelection  Whether to include selected elements
 * @param {Array}   selection         Current selection from AiAssistant (preferred over LF query)
 * @returns {Promise<object>}         AiContext schema
 */
export async function buildContext(includeSelection = true, selection = null) {
  const flow = window.epxFlow;
  if (!flow) {
    return { current_xml: '', selected_elements: [], process_name: '', language: _detectLang() };
  }

  let xml = '';
  try {
    const result = await flow.exportXML();
    xml = result.xml || '';
  } catch (e) {
    console.warn('[AiContext] exportXML failed:', e);
  }

  const trimmed = xml.length > MAX_XML_CHARS
    ? xml.slice(0, MAX_XML_CHARS) + '\n<!-- truncated -->'
    : xml;

  // Use provided selection snapshot if available; otherwise fall back to LF model query
  let selectedElements = [];
  if (includeSelection) {
    if (selection && selection.length > 0) {
      selectedElements = selection;
    } else {
      try {
        const lf = flow.getLogicFlow();
        const nodes = lf.graphModel.nodes.filter(n => n.isSelected);
        const edges = lf.graphModel.edges.filter(e => e.isSelected);
        selectedElements = [...nodes, ...edges];
      } catch (_) {}
    }
  }

  return {
    current_xml: trimmed,
    selected_elements: selectedElements.map(el => ({
      id: el.id,
      type: el.type || '',
      name: el.properties?.name || el.text?.value || '',
    })),
    process_name: _getRootName(xml),
    language: _detectLang(),
  };
}

/**
 * Builds the context object for a DMN AssistRequest (Themis identity).
 *
 * THEMIS_CHAT_PLAN §3.1 — Themis context contract:
 *   Adds to the base BPMN shape (current_xml / selected_elements / process_name
 *   / language) all the structured DMN signals Themis needs to explain,
 *   validate, and generate scenarios without re-parsing XML server-side:
 *   - `mode: 'rules'` and `identity: 'themis'` — authoritative routing flags.
 *   - `view` and `decision_ref` — what the user is looking at.
 *   - `drg_summary` — inputs, decisions, BKMs, knowledge sources (typed).
 *   - `simulator_state` — scenarios loaded / last run snapshot.
 *   - `expression` and `open_expression` — focused boxed expression details
 *     (rules array serialized when a decision table is open).
 *   - `validator_issues[]` — attached on every Themis turn (capped) so the
 *     LLM can diagnose without re-running the validator.
 *   - `scope` — friendly labels for the active decision.
 *
 * @param {string} [decisionRef]  The decisionRef property of the active BusinessRuleTask
 * @param {object} [opts]         { intent }  optional — used by callers that
 *                                want to gate validator_issues by intent.
 * @returns {object}  AiContext schema
 */
export async function buildDmnContext(decisionRef = '', opts = {}) {
  let xml = '';
  try {
    const result = window.epxDmn?.exportXML?.();
    // exportXML may be sync (test mock) or async (production EpxDmn). Await
    // unconditionally — `await` on a non-Promise value just returns it.
    const resolved = result && typeof result.then === 'function' ? await result : result;
    xml = resolved?.xml || '';
  } catch (e) {
    console.warn('[AiContext] DMN exportXML failed:', e);
  }

  const wasTruncated = xml.length > MAX_XML_CHARS;
  const trimmed = wasTruncated
    ? xml.slice(0, MAX_XML_CHARS) + '\n<!-- truncated -->'
    : xml;

  const dmn = window.epxDmn;
  const view = (dmn && typeof dmn.getView === 'function') ? dmn.getView() : 'drd';

  // Resolve the BR Task's `decisionRef` (often a human-readable label) to a
  // real DRG element. `newDecision()` autogenerates an id like Decision_<rng>
  // while the BPMN side keeps the user-typed name, so id-only lookup misses.
  const decision = _resolveDecisionByRef(dmn, decisionRef);
  // If single Decision is selected in the DRD and no decisionRef was given,
  // use the selection as the focused decision so the LLM can answer "esta
  // decisión" without the user spelling it out.
  const focusFromSelection = !decision ? _decisionFromSingleSelection(dmn) : null;
  const focused = decision || focusFromSelection;
  const focusedId   = focused?.id || null;
  const focusedName = focused?.name || null;
  const inline = _collectInlineState(dmn);

  const ctx = {
    current_xml: trimmed,
    current_xml_truncated: wasTruncated,
    selected_elements: _collectDmnSelection(dmn),
    process_name: focusedName || decisionRef || 'Decision Table',
    language: _detectLang(),
    mode: 'rules',
    identity: 'themis',
    view,
    // `decision_ref` is now the resolved id (the one that actually exists in
    // current_xml). Keep the original alias separately so the backend can
    // surface it back to the user if the resolution missed.
    decision_ref:       focusedId || decisionRef || null,
    // Only attach the alias when an actual resolution happened (the ref was
    // a label that mapped to a different real id). When the ref couldn't be
    // resolved at all, the original is already in `decision_ref`.
    decision_ref_alias: focusedId && decisionRef && decisionRef !== focusedId ? decisionRef : null,
    focused_decision_id: focusedId,
    drg_summary: _buildDrgSummary(dmn),
    simulator_state: _buildSimulatorState(dmn),
  };

  // Active boxed-expression hint — picks up the inline editor inside the DRD
  // too, not just the standalone EXPRESSION view.
  const expression = _activeExpressionContext(dmn, view, inline);
  if (expression) ctx.expression = expression;

  // Open decision table / expression with rules serialized for explanation
  // and coverage analysis (THEMIS_CHAT_PLAN §3.1 — open_expression).
  const openExpr = _buildOpenExpression(dmn, view, focused, inline);
  if (openExpr) ctx.open_expression = openExpr;

  // Inline overlay (popup inside DRD) — surface as its own field so the
  // backend doesn't have to infer it from view===drd + open_expression.
  if (inline) ctx.inline = inline;

  // Scope = friendly labels carried separately so the LLM can address the
  // user without re-deriving them from the XML.
  if (focusedId || decisionRef) {
    ctx.scope = {
      decisionId:   focusedId || decisionRef,
      decisionName: focusedName || decisionRef,
      view,
      inline_kind: inline?.kind || null,
    };
  }

  // Validator issues — attached on every Themis turn so the LLM has a
  // structural baseline for explanations. Capped at 50 inside the helper.
  const issues = _collectValidatorIssues(dmn);
  if (issues.length) ctx.validator_issues = issues;

  return ctx;
}

/**
 * Alias kept so callers that already know the Themis identity can be explicit.
 * Functionally identical to {@link buildDmnContext}.
 */
export const buildThemisContext = buildDmnContext;

function _collectDmnSelection(dmn) {
  if (!dmn || typeof dmn.getSelection !== 'function') return [];
  try {
    const sel = dmn.getSelection() || [];
    return sel.map((el) => ({
      id:   el.id || '',
      type: el.type || (el.businessObject && el.businessObject.$type) || '',
      name: (el.businessObject && el.businessObject.name) || el.name || '',
    }));
  } catch (_) { return []; }
}

function _resolveDecisionName(dmn, decisionRef) {
  const found = _resolveDecisionByRef(dmn, decisionRef);
  return found?.name || null;
}

/**
 * Resolve a `decisionRef` (which can be either the technical id from the BR
 * Task properties, the user-facing decision name, or a stale string) to a
 * real DRG element from the active model. Returns the moddle node, or null
 * when no match exists.
 */
function _resolveDecisionByRef(dmn, decisionRef) {
  if (!dmn || !decisionRef) return null;
  try {
    if (typeof dmn.findDecisionByIdOrName === 'function') {
      const found = dmn.findDecisionByIdOrName(decisionRef);
      if (found) return found;
    }
    if (typeof dmn.getDecisionById === 'function') {
      const byId = dmn.getDecisionById(decisionRef);
      if (byId) return byId;
    }
    // Fall back to scanning getModel() / getDefinitions() when the public
    // helper is missing (older builds, partial mocks in tests).
    const lower = String(decisionRef).toLowerCase();
    const model = typeof dmn.getModel === 'function' ? dmn.getModel() : null;
    const els = (model && Array.isArray(model.drgElements)) ? model.drgElements : [];
    const fromModel = els.find((el) => el && (el.id === decisionRef || el.name === decisionRef
      || (typeof el.name === 'string' && el.name.toLowerCase() === lower)));
    if (fromModel) return fromModel.raw || fromModel;
    if (typeof dmn.getDefinitions === 'function') {
      const defs = dmn.getDefinitions();
      const drg = (defs && Array.isArray(defs.drgElement)) ? defs.drgElement : [];
      return drg.find((el) => el && (el.id === decisionRef || el.name === decisionRef
        || (typeof el.name === 'string' && el.name.toLowerCase() === lower))) || null;
    }
  } catch (_) { /* swallow */ }
  return null;
}

/**
 * When the user has a single Decision selected in the DRD and the caller
 * didn't pass a `decisionRef`, the selection IS the focus. Returns the
 * moddle decision (or null) without throwing.
 */
function _decisionFromSingleSelection(dmn) {
  if (!dmn || typeof dmn.getSelection !== 'function') return null;
  try {
    const sel = dmn.getSelection() || [];
    if (sel.length !== 1) return null;
    const el = sel[0];
    const $type = (el.businessObject && el.businessObject.$type) || el.type || '';
    if (!$type.endsWith(':Decision')) return null;
    return el.businessObject || _resolveDecisionByRef(dmn, el.id);
  } catch (_) { return null; }
}

/**
 * Inline overlay state (popup inside DRD that hosts a Decision Table or a
 * Boxed Expression editor without leaving the DRD view). Returns null when
 * no inline editor is mounted.
 */
function _collectInlineState(dmn) {
  if (!dmn || typeof dmn.getInlineEditor !== 'function') return null;
  const ed = dmn.getInlineEditor();
  if (!ed) return null;
  try {
    const dec  = typeof ed.getDecision   === 'function' ? ed.getDecision()   : null;
    const bkm  = typeof ed.getBkm        === 'function' ? ed.getBkm()        : null;
    const expr = typeof ed.getExpression === 'function' ? ed.getExpression() : null;
    const target = dec || bkm || null;
    const logic  = (target && target.decisionLogic) || expr || null;
    const $type  = (logic && logic.$type) || '';
    return {
      kind:           _kindFromType($type) || 'table',
      decision_id:    target?.id || null,
      decision_name:  target?.name || null,
      expression_id:  logic?.id || null,
    };
  } catch (_) { return null; }
}

function _activeExpressionContext(dmn, view, inline) {
  // Inline overlay short-circuits: even though `view` is still 'drd', the
  // user is editing a boxed expression — surface its kind/content so chips
  // and the prompt focus on it.
  if (inline) {
    try {
      const ed = typeof dmn.getInlineEditor === 'function' ? dmn.getInlineEditor() : null;
      const expr = ed && typeof ed.getExpression === 'function' ? ed.getExpression() : null;
      const dec  = ed && typeof ed.getDecision   === 'function' ? ed.getDecision()   : null;
      const logic = expr || (dec && dec.decisionLogic) || null;
      if (!logic) return null;
      return {
        kind: inline.kind,
        content: _extractFeelText(logic) || null,
        expr_path: logic.id || null,
      };
    } catch (_) { return null; }
  }
  if (!dmn || view !== 'expression') return null;
  try {
    const ed = typeof dmn.getActiveEditor === 'function' ? dmn.getActiveEditor() : null;
    if (!ed) return null;
    const expr = (typeof ed.getExpression === 'function')
      ? ed.getExpression()
      : (typeof ed.getDecision === 'function' ? (ed.getDecision()?.decisionLogic || null) : null);
    if (!expr) return null;
    const $type = expr.$type || '';
    const kind = _kindFromType($type);
    const content = _extractFeelText(expr);
    return {
      kind,
      content: content || null,
      expr_path: expr.id || null,
    };
  } catch (_) { return null; }
}

function _kindFromType($type) {
  if (!$type) return null;
  if ($type.endsWith(':LiteralExpression'))         return 'literal';
  if ($type.endsWith(':Context'))                   return 'context';
  if ($type.endsWith(':List'))                      return 'list';
  if ($type.endsWith(':Invocation'))                return 'invocation';
  if ($type.endsWith(':Relation'))                  return 'relation';
  if ($type.endsWith(':FunctionDefinition'))        return 'function';
  if ($type.endsWith(':DecisionTable'))             return 'decisionTable';
  if ($type === 'epx:MachineLearningDecision')      return 'ml';
  return null;
}

function _extractFeelText(expr) {
  if (!expr) return '';
  if (expr.text && typeof expr.text === 'string') return expr.text;
  // For literal expression the FEEL lives in `text` on the moddle node.
  if (expr.$type && expr.$type.endsWith(':LiteralExpression')) {
    return (expr.text && (expr.text.text || expr.text)) || '';
  }
  return '';
}

/**
 * Structured DRG summary — inputs / decisions / BKMs / knowledge sources with
 * typing info so the LLM can reason about coverage and types without re-parsing
 * the XML. Returns null-safe lists; never throws.
 */
function _buildDrgSummary(dmn) {
  const out = { inputs: [], decisions: [], bkms: [], knowledge_sources: [] };
  if (!dmn) return out;

  let elements = [];
  try {
    if (typeof dmn.getModel === 'function') {
      const model = dmn.getModel();
      elements = (model && Array.isArray(model.drgElements)) ? model.drgElements : [];
    }
    if (!elements.length && typeof dmn.getDefinitions === 'function') {
      const defs = dmn.getDefinitions();
      elements = (defs && Array.isArray(defs.drgElement)) ? defs.drgElement : [];
    }
  } catch (_) { /* swallow — best effort */ }

  for (const el of elements) {
    if (!el) continue;
    const $type = el.$type || '';
    if ($type.endsWith(':InputData')) {
      out.inputs.push({
        id:       el.id || '',
        name:     el.name || el.id || '',
        type_ref: (el.variable && el.variable.typeRef) || el.typeRef || null,
      });
    } else if ($type.endsWith(':Decision')) {
      const dl = el.decisionLogic || null;
      const logic = _summarizeDecisionLogic(dl);
      out.decisions.push({
        id:        el.id || '',
        name:      el.name || el.id || '',
        type_ref:  (el.variable && el.variable.typeRef) || null,
        ...logic,
      });
    } else if ($type.endsWith(':BusinessKnowledgeModel')) {
      out.bkms.push({
        id:   el.id || '',
        name: el.name || el.id || '',
      });
    } else if ($type.endsWith(':KnowledgeSource')) {
      out.knowledge_sources.push({
        id:   el.id || '',
        name: el.name || el.id || '',
      });
    }
  }

  return out;
}

function _summarizeDecisionLogic(dl) {
  if (!dl || !dl.$type) return { logic_type: null };
  const $type = dl.$type;
  if ($type === 'epx:MachineLearningDecision') {
    return {
      logic_type:    'ml',
      ml_model_ref:  dl.modelRef || dl.model_ref || null,
    };
  }
  if ($type.endsWith(':DecisionTable')) {
    return {
      logic_type:     'decisionTable',
      hit_policy:     dl.hitPolicy || 'UNIQUE',
      rule_count:     Array.isArray(dl.rule)   ? dl.rule.length   : 0,
      input_clauses:  (Array.isArray(dl.input)  ? dl.input  : []).map((c) => c.label || c.inputExpression?.text || c.id || ''),
      output_clauses: (Array.isArray(dl.output) ? dl.output : []).map((c) => c.label || c.name || c.id || ''),
    };
  }
  if ($type.endsWith(':LiteralExpression'))   return { logic_type: 'literal' };
  if ($type.endsWith(':Context'))             return { logic_type: 'context' };
  if ($type.endsWith(':List'))                return { logic_type: 'list' };
  if ($type.endsWith(':Invocation'))          return { logic_type: 'invocation' };
  if ($type.endsWith(':Relation'))            return { logic_type: 'relation' };
  if ($type.endsWith(':FunctionDefinition'))  return { logic_type: 'function' };
  return { logic_type: null };
}

/**
 * Snapshot of the simulator (THEMIS_CHAT_PLAN §3.1 — simulator_state).
 * Only reads through the public `dmn.simulator` surface; never opens the dock.
 */
function _buildSimulatorState(dmn) {
  const fallback = { available: false, scenarios_loaded: 0, last_run: null };
  if (!dmn || !dmn.simulator) return fallback;
  const sim = dmn.simulator;
  try {
    const available = typeof sim.isAvailable === 'function' ? !!sim.isAvailable() : false;
    const scenarios = (typeof sim.getScenarios === 'function') ? (sim.getScenarios() || []) : [];
    const results   = (typeof sim.getResults   === 'function') ? (sim.getResults()   || []) : [];
    const last_run  = results.length
      ? {
          total:  results.length,
          passed: results.filter((r) => r && (r.ok === true || r.passed === true)).length,
          failed: results.filter((r) => r && (r.ok === false || r.passed === false)).length,
        }
      : null;
    return {
      available,
      open: typeof sim.isOpen === 'function' ? !!sim.isOpen() : false,
      scenarios_loaded: scenarios.length,
      last_run,
    };
  } catch (_) {
    return fallback;
  }
}

/**
 * Serializes the active boxed expression for table view (rules with FEEL
 * cells) so the LLM can answer "what does this rule do?" / "is this rule
 * unreachable?" without re-parsing the XML.
 *
 * Returns null when no editor is open or the active editor doesn't expose a
 * structure we can read.
 */
function _buildOpenExpression(dmn, view, focusedDecision, inline) {
  if (!dmn) return null;
  // No view-level editor + no inline overlay = nothing to focus on.
  if (view === 'drd' && !inline && !focusedDecision) return null;
  let dec = null;
  try {
    if (inline) {
      // Inline overlay wins — it's what the user is actually looking at.
      const ed = typeof dmn.getInlineEditor === 'function' ? dmn.getInlineEditor() : null;
      dec = ed && typeof ed.getDecision === 'function' ? ed.getDecision() : null;
    } else if (view !== 'drd') {
      const ed = typeof dmn.getActiveEditor === 'function' ? dmn.getActiveEditor() : null;
      dec = ed && typeof ed.getDecision === 'function' ? ed.getDecision() : null;
    }
    if (!dec) dec = focusedDecision || null;
  } catch (_) { return null; }
  const dl = dec && dec.decisionLogic;
  if (!dl || !dl.$type) return null;

  const summary = _summarizeDecisionLogic(dl);
  const out = {
    decision_id:   dec.id || null,
    decision_name: dec.name || dec.id || null,
    ...summary,
  };

  if (dl.$type.endsWith(':DecisionTable') && Array.isArray(dl.rule)) {
    // Cap to first 20 rules so the prompt stays bounded.
    out.rules = dl.rule.slice(0, 20).map((r, idx) => ({
      index:         idx,
      id:            r.id || null,
      input_entries: (Array.isArray(r.inputEntry)  ? r.inputEntry  : []).map((c) => _feelText(c)),
      output_entries:(Array.isArray(r.outputEntry) ? r.outputEntry : []).map((c) => _feelText(c)),
      annotation:    r.description || null,
    }));
  }
  return out;
}

function _feelText(cell) {
  if (!cell) return '';
  if (typeof cell.text === 'string') return cell.text;
  if (cell.text && typeof cell.text.text === 'string') return cell.text.text;
  return '';
}

function _collectValidatorIssues(dmn) {
  if (!dmn) return [];
  try {
    const validator = typeof dmn.getValidator === 'function' ? dmn.getValidator() : null;
    const defs = typeof dmn.getDefinitions === 'function' ? dmn.getDefinitions() : null;
    if (!validator || !defs) return [];
    const raw = validator.validate(defs) || [];
    return raw.slice(0, 50).map((iss) => ({
      element_id: iss.elementId || iss.element_id || null,
      code:       iss.code || null,
      message:    iss.message || iss.description || '',
      severity:   iss.severity || 'warning',
    }));
  } catch (_) { return []; }
}

function _getRootName(xml) {
  // Try <process name="..."> first, then <definitions name="...">
  const m = xml.match(/<process[^>]+name="([^"]+)"/);
  if (m) return m[1];
  const m2 = xml.match(/name="([^"]+)"/);
  return m2 ? m2[1] : 'Proceso';
}

function _detectLang() {
  return 'es';
}
